import QRCode from 'qrcode'
import { ApiError, api } from './api.js'
import { esc } from './format.js'

/**
 * Account setup, reached from the one-time link in a setup email.
 *
 * Three steps, in this order for a reason:
 *
 *   1. choose a password
 *   2. scan the authenticator app and prove a code works
 *   3. save the recovery codes
 *
 * The link is only spent at step 2, so a half-finished setup can be resumed
 * from the same link rather than needing a new one. The recovery codes are
 * shown once, at the end, and cannot be retrieved afterwards.
 */

interface SetupStarted {
  email: string
  name: string
  totpUri: string
  manualEntryKey: string
}

const $ = <T extends HTMLElement>(selector: string): T =>
  document.querySelector<T>(selector) as T

export function isSetupUrl(): boolean {
  return window.location.pathname.replace(/\/+$/, '') === '/setup'
}

export function openSetup(): void {
  const token = new URLSearchParams(window.location.search).get('token') ?? ''

  document.body.innerHTML = shell(
    token
      ? passwordStep()
      : `<p class="err">This link is missing its token. Ask an owner for a new setup link.</p>`,
  )

  if (!token) return
  wirePasswordStep(token)
}

function shell(inner: string): string {
  return `
    <main class="setup">
      <div class="setup-brand">
        <span class="brandmark"><img src="/ni-logo.png" alt="Network Intelligence"></span>
      </div>
      <h1>NI Money Manager</h1>
      <p class="sub">Set up your account</p>
      <div id="setupBody">${inner}</div>
    </main>
    <style>
      .setup{max-width:30rem;margin:5vh auto;padding:1.5rem;font-family:var(--body,system-ui)}
      .setup-brand{display:flex;justify-content:center;margin-bottom:1.25rem}
      .setup-brand .brandmark{display:inline-flex;align-items:center;justify-content:center;
        background:#1e2a2c;border-radius:14px;padding:16px 20px;max-width:100%}
      .setup-brand img{display:block;width:100%;max-width:340px;height:auto}
      .setup h1{margin:0;font-family:var(--display,Georgia,serif);font-size:28px}
      .setup .sub{color:var(--muted,#5d6a69);margin:.25rem 0 1.5rem}
      .setup .field{margin-bottom:1rem;display:flex;flex-direction:column;gap:.35rem}
      .setup input{padding:.6rem;border:1px solid var(--line,#ccc);border-radius:.4rem;font:inherit}
      .setup .err{color:var(--bad,#b0443a);margin:.5rem 0;min-height:1.2em}
      .setup .hint{font-size:.85em;color:var(--muted,#5d6a69);margin:.25rem 0 0}
      .setup .btn{border:1px solid var(--line,#ccc);background:var(--panel,#fff);border-radius:8px;
        padding:.6rem 1rem;cursor:pointer;font-weight:600}
      .setup .btn[disabled]{opacity:.55;cursor:default}
      .setup ol{padding-left:1.2rem;line-height:1.6}
      .qrwrap{display:flex;justify-content:center;margin:1rem 0}
      .qrwrap img{width:220px;height:220px;background:#fff;padding:10px;border-radius:10px}
      .manualkey{font-family:var(--num,monospace);word-break:break-all;background:var(--panel-2,#f4f4f4);
        padding:.6rem;border-radius:.4rem;font-size:.9em;text-align:center}
      .codes{display:grid;grid-template-columns:1fr 1fr;gap:.4rem;margin:1rem 0;
        font-family:var(--num,monospace);font-size:1.05em}
      .codes span{background:var(--panel-2,#f4f4f4);padding:.5rem;border-radius:.3rem;text-align:center}
      .warnbox{background:var(--warn-soft,#f8ecd6);border-radius:.5rem;padding:.9rem;margin:1rem 0}
    </style>`
}

// ------------------------------------------------------------ 1. password

function passwordStep(): string {
  return `
    <form id="pwForm" novalidate>
      <p>Choose a password for your account. It needs at least 9 characters,
         and must not contain your name or email address.</p>
      <div class="field">
        <label for="su-password">Password</label>
        <input id="su-password" type="password" autocomplete="new-password" required>
      </div>
      <div class="field">
        <label for="su-confirm">Type it again</label>
        <input id="su-confirm" type="password" autocomplete="new-password" required>
      </div>
      <p class="err" id="su-error" role="alert"></p>
      <button class="btn" type="submit" id="su-submit">Continue</button>
    </form>`
}

function wirePasswordStep(token: string): void {
  const error = $('#su-error')
  const submit = $<HTMLButtonElement>('#su-submit')

  $('#pwForm').addEventListener('submit', (event) => {
    event.preventDefault()
    error.textContent = ''

    const password = $<HTMLInputElement>('#su-password').value
    if (password !== $<HTMLInputElement>('#su-confirm').value) {
      error.textContent = 'Those two passwords are not the same.'
      return
    }

    submit.disabled = true
    void api
      .post<SetupStarted>('/api/auth/setup', { token, password })
      .then((started) => showEnrolStep(token, started))
      .catch((problem: unknown) => {
        // The policy returns every reason at once, so all of them are shown.
        error.textContent =
          problem instanceof ApiError ? problem.message : 'Could not save that password.'
      })
      .finally(() => {
        submit.disabled = false
      })
  })
}

// ------------------------------------------------------------ 2. authenticator

function showEnrolStep(token: string, started: SetupStarted): void {
  $('#setupBody').innerHTML = `
    <p>Hello ${esc(started.name)}. Now set up two-factor sign-in — it is required,
       and it is what keeps the books safe if your password is ever guessed.</p>
    <ol>
      <li>Open your authenticator app (Google Authenticator, Authy, 1Password,
          Microsoft Authenticator — any of them).</li>
      <li>Scan this code.</li>
      <li>Type the 6-digit number it shows.</li>
    </ol>
    <div class="qrwrap"><img id="su-qr" alt="Two-factor setup code"></div>
    <p class="hint">Cannot scan it? Enter this key by hand instead:</p>
    <p class="manualkey">${esc(started.manualEntryKey)}</p>
    <form id="codeForm" novalidate>
      <div class="field">
        <label for="su-code">6-digit code</label>
        <input id="su-code" inputmode="numeric" autocomplete="one-time-code" maxlength="7" required>
      </div>
      <p class="err" id="su-error" role="alert"></p>
      <button class="btn" type="submit" id="su-confirm-btn">Confirm</button>
    </form>`

  // Rendered in the browser so the secret is never sent anywhere else.
  void QRCode.toDataURL(started.totpUri, { margin: 1, width: 440 })
    .then((url) => {
      $<HTMLImageElement>('#su-qr').src = url
    })
    .catch(() => {
      $('#su-qr').replaceWith(
        Object.assign(document.createElement('p'), {
          className: 'hint',
          textContent: 'Could not draw the code — use the key below instead.',
        }),
      )
    })

  const error = $('#su-error')
  const confirm = $<HTMLButtonElement>('#su-confirm-btn')

  $('#codeForm').addEventListener('submit', (event) => {
    event.preventDefault()
    error.textContent = ''
    confirm.disabled = true

    void api
      .post<{ recoveryCodes: string[] }>('/api/auth/setup/confirm', {
        token,
        code: $<HTMLInputElement>('#su-code').value.trim(),
      })
      .then((done) => showRecoveryCodes(done.recoveryCodes))
      .catch((problem: unknown) => {
        error.textContent =
          problem instanceof ApiError ? problem.message : 'Could not confirm that code.'
      })
      .finally(() => {
        confirm.disabled = false
      })
  })

  $<HTMLInputElement>('#su-code').focus()
}

// ------------------------------------------------------------ 3. recovery codes

function showRecoveryCodes(codes: string[]): void {
  $('#setupBody').innerHTML = `
    <p><b>You are set up.</b> One last thing, and it matters.</p>
    <div class="warnbox">
      <b>Save these recovery codes now.</b> They are shown once and cannot be
      shown again. If you lose your phone, one of these is the only way back
      into your account without asking the other owner to reset it.
    </div>
    <div class="codes">${codes.map((code) => `<span>${esc(code)}</span>`).join('')}</div>
    <p class="hint">Print them, or put them in your password manager. Each one
       works once.</p>
    <p><button class="btn" id="su-print" type="button">Print</button>
       <button class="btn" id="su-done" type="button">I have saved them — continue</button></p>`

  $('#su-print').addEventListener('click', () => window.print())
  $('#su-done').addEventListener('click', () => {
    // Setup already signed them in, so this lands on the month view.
    window.location.href = '/'
  })
}
